<template>
  <v-container fluid class="assets-list-container">
    <v-card outlined class="assets-list-card">
      <div class="assets-list-header pa-4 d-flex align-center">
        <v-divider class="header-divider ml-1 mr-2" vertical />
        <div class="flex-grow-1">
          <div class="text-subtitle-1 font-weight-medium"><v-icon class="mr-3">mdi-package-variant-closed</v-icon>{{ $t('deliverables_list') }}</div>
          <div class="text-caption grey--text">
            {{ baseInstances.length }} {{ $t('deliverable_singular') }} · {{ totalFormats }} {{ $t('formats') }}
            <template v-if="totalValue > 0"> · <span class="primary--text font-weight-medium">{{ formatCurrency(totalValue) }}</span></template>
          </div>
        </div>
        <v-btn text small class="assets-list-toggle" @click="toggleAll"><v-icon left>{{ allRowsVisible ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon><span class="assets-list-toggle-label">{{ allRowsVisible ? $t('close_all') : $t('open_all') }}</span></v-btn>
        <v-btn-toggle mandatory dense value="assets-list" class="deliverable-nav-toggle assets-list-nav">
          <v-btn value="assets-list" color="primary" icon :title="$t('deliverables_list')" :aria-label="$t('deliverables_list')"><v-icon color="primary">mdi-format-list-bulleted</v-icon></v-btn>
          <v-btn value="deliverables" icon :title="$t('deliverables_list')" :aria-label="$t('deliverables_list')" @click="goTo('JobDeliverableManagement')"><v-icon>mdi-format-list-text</v-icon></v-btn>
          <v-btn value="assets" icon :title="$t('deliverable_assets')" :aria-label="$t('deliverable_assets')" @click="goTo('JobDeliverableAssets')"><v-icon>mdi-list-status</v-icon></v-btn>
        </v-btn-toggle>
      </div>

      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-data-table v-else :headers="headers" :items="rows" :items-per-page="itemsPerPage" :footer-props="$defaultDataTableFooterProps" :mobile-breakpoint="0" class="assets-table">
        <template v-slot:item.deliverable="{ item }">{{ item.deliverable }}</template>
        <template v-slot:item.asset="{ item }">
          <template v-if="item.asset">
            <span class="asset-name" :title="item.asset.name">{{ item.asset.name }}</span>
            <span class="asset-badges ml-2">
              <span class="asset-version-badge">{{ versionLabel(item.asset.version) }}</span>
              <v-badge
                :value="Boolean(item.asset.annotationCount)"
                :content="item.asset.annotationCount"
                color="error"
                overlap
                class="approval-count-badge"
              >
                <span class="asset-approval-badge" :class="`asset-approval-badge--${item.asset.status}`">
                  <v-icon class="asset-approval-icon">{{ approvalIcon(item.asset.status) }}</v-icon>
                </span>
              </v-badge>
            </span>
          </template>
          <span v-else class="grey--text">({{ $t('no_file_attached') || 'nenhum arquivo anexado' }})</span>
        </template>
        <template v-slot:item.status="{ item }"><v-chip small label>{{ item.status }}</v-chip></template>
        <template v-slot:item.actions="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }"><v-btn icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-horizontal</v-icon></v-btn></template>
            <v-list dense>
              <v-list-item v-if="item.asset && canEditMarkup" @click="openNewVersionUpload(item)"><v-list-item-icon class="mr-2"><v-icon>mdi-file-replace-outline</v-icon></v-list-item-icon><v-list-item-title>{{ $t('markup.upload_new_version') || 'Atualizar arquivo' }}</v-list-item-title></v-list-item>
              <v-list-item v-if="item.asset" @click="openApproval(item.asset)"><v-list-item-icon class="mr-2"><v-icon>mdi-list-status</v-icon></v-list-item-icon><v-list-item-title>{{ $t('markup.approval') }}</v-list-item-title></v-list-item>
              <v-list-item v-if="item.asset" @click="download(item.asset)"><v-list-item-icon class="mr-2"><v-icon>mdi-download-outline</v-icon></v-list-item-icon><v-list-item-title>{{ $t('download') }} ({{ $t('current') || 'atual' }})</v-list-item-title></v-list-item>
              <v-list-item v-else-if="canEditMarkup" @click="goToUpload"><v-list-item-icon class="mr-2"><v-icon>mdi-upload</v-icon></v-list-item-icon><v-list-item-title>{{ $t('markup.upload_file') || 'Enviar arquivo' }}</v-list-item-title></v-list-item>
            </v-list>
          </v-menu>
        </template>
        <template v-slot:no-data><div class="py-10 text-center grey--text"><v-icon large color="grey lighten-1">mdi-package-variant-closed</v-icon><div class="mt-2">{{ $t('no_deliverables_added') }}</div></div></template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="approvalDialog" max-width="90vw" max-height="90vh" content-class="asset-approval-dialog">
      <v-card><v-card-title>{{ selectedAsset && selectedAsset.name }}<v-spacer /><v-btn icon @click="approvalDialog = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title>
        <v-card-text class="pa-0" style="height:80vh"><MarkupSession v-if="selectedAsset" ref="markupSession" :key="selectedAsset.sessionId" :session-id="selectedAsset.sessionId" :hide-toolbar="true" :can-edit-markup="canEditMarkup" :use-job-approval-video-player="true" @markup-count-updated="updateMarkupCount" /></v-card-text>
      </v-card>
    </v-dialog>

    <FormatAssetManagement
      v-if="assetForVersionUpload"
      ref="versionUploader"
      class="d-none"
      :format-instance="assetForVersionUpload.formatInstance"
      :job-id="jobId"
      :readonly="isJobReadonly"
      :can-edit-markup="canEditMarkup"
      @asset-uploaded="loadData"
    />
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import JobDeliverableAPI from '@/services/api/job/deliverable'
import JobDeliverAssetAPI from '@/services/api/job/job_deliver_asset'
import StatusAPI from '@/services/api/business/status'
import LaneAPI from '@/services/api/job/lane'
import PoolACLAPI from '@/services/api/job/pool_acl'
import ContentActions from '@/store/content/actions/actions-types'
import MarkupSession from '@/views/content/markup/MarkupSession.vue'
import FormatAssetManagement from '@/components/job/FormatAssetManagement.vue'

export default {
  name: 'DeliverableAssetsListManagement',
  components: { MarkupSession, FormatAssetManagement },
  props: {
    jobId: { type: [String, Number], required: true },
    customerId: { type: [String, Number], default: null },
    jobData: { type: Object, default: () => ({}) },
    embedded: { type: Boolean, default: false }
  },
  data: () => ({ loading: false, baseInstances: [], statusOptions: [], approvalDialog: false, selectedAsset: null, assetForVersionUpload: null, itemsPerPage: 10, jobAcl: 0 }),
  computed: {
    headers() { return [
      { text: this.$t('deliverable'), value: 'deliverable' }, { text: this.$t('customer_item') || 'Item do cliente', value: 'customerItem' },
      { text: this.$t('format'), value: 'format' }, { text: this.$t('title'), value: 'title' }, { text: 'Asset (arquivo)', value: 'asset' },
      { text: this.$t('format_status') || 'Status do formato', value: 'status' }, { text: this.$t('actions'), value: 'actions', sortable: false }
    ] },
    rows() {
      return this.baseInstances.flatMap(base => (base.formatInstances || []).flatMap(format => {
        const common = { deliverable: base.base_title, customerItem: base.customer_item_title || base.base_title, format: format.format_desc || format.format_title || '-', title: format.format_title || format.format_desc || '-', status: this.statusTitle(format.status_id), formatInstance: format }
        return format.assets?.length ? format.assets.map(asset => ({ ...common, asset })) : [{ ...common, asset: null }]
      }))
    },
    totalFormats() { return this.baseInstances.reduce((total, base) => total + (base.formatInstances || []).length, 0) },
    totalValue() { return this.baseInstances.reduce((total, base) => total + (base.formatInstances || []).reduce((sum, format) => sum + (format.billableInstances || []).reduce((billableTotal, billable) => billableTotal + (Number(billable.quantity) || 0) * (Number(billable.price) || 0), 0), 0), 0) },
    effectiveJobId() { return this.jobId || this.$route.params.jobId || null },
    effectiveCustomerId() { return this.customerId || this.jobData.customer_id || null },
    isJobReadonly() { return !!this.jobData?.readonly },
    canEditMarkup() { return !this.isJobReadonly && (this.jobAcl & 512) === 512 },
    allRowsVisible() { return this.itemsPerPage === -1 }
  },
  watch: {
    effectiveCustomerId: {
      immediate: true,
      handler(customerId) {
        if (customerId && this.effectiveJobId) this.loadData()
      }
    },
    effectiveJobId: {
      immediate: true,
      handler(jobId) {
        if (jobId && this.effectiveCustomerId) this.loadData()
      }
    },
    approvalDialog(isOpen, wasOpen) {
      if (!isOpen && wasOpen) this.$refs.markupSession?.emitMarkupCount()
    }
  },
  methods: {
    ...mapActions('content/actions', [ContentActions.DOWNLOAD_FILE]),
    async loadData() {
      if (!this.effectiveJobId) return

      this.loading = true
      try {
        const [deliverables, statuses] = await Promise.all([JobDeliverableAPI.getJobDeliverables(this.effectiveJobId), this.effectiveCustomerId ? StatusAPI.getAllStatusesByCustomer(this.effectiveCustomerId, { limit: 1000 }) : Promise.resolve({ data: { items: [] } }), this.loadJobAcl()])
        this.$emit('acl-loaded', this.jobAcl)
        this.statusOptions = statuses.data.items || []
        const items = Array.isArray(deliverables.data) ? deliverables.data : (deliverables.data.items || [])
        this.baseInstances = await Promise.all(items.filter(item => item.base_instance).map(async item => {
          const base = { ...item.base_instance }
          base.formatInstances = await Promise.all((item.format_instances || []).map(async entry => {
            const format = { ...entry.format_instance, job_deliver: entry.job_deliver, format_desc: entry.format_instance.format_desc || entry.format_instance.format }
            format.billableInstances = entry.billable_instances || entry.billableInstances || []
            if (format.job_deliver?.job_deliver_id) {
              try {
                format.assets = await this.loadAssets(format.job_deliver.job_deliver_id)
              } catch (error) {
                console.error('Error loading format assets:', error)
                format.assets = []
              }
            } else {
              format.assets = []
            }
            return format
          }))
          return base
        }))
      } catch (error) {
        console.error('Error loading deliverable asset list:', error)
        this.baseInstances = []
      } finally { this.loading = false }
    },
    async loadAssets(jobDeliverId) {
      const response = await JobDeliverAssetAPI.readAssets(jobDeliverId, this.effectiveJobId)
      const raw = Array.isArray(response.data) ? response.data : response.data?.job_deliver_asset
      const values = Array.isArray(raw) ? raw : (raw ? [raw] : [])
      return values.map(item => {
        const session = item.markup_session || {}
        const nodes = session.markup_session_node || []
        const latest = nodes.reduce((best, node) => !best || Number(node.version) > Number(best.version) ? node : best, null)
        // The Content API omits nodes that the current user cannot access.
        // Do not render their session as an asset row without an accessible node.
        if (!latest?.node_id) return null
        const status = latest?.status || latest?.approval_status || 'pending'
        return { name: session.name || this.$t('markup.unnamed_asset'), sessionId: session.markup_session_id || session.id, markup_session_id: session.markup_session_id || session.id, nodeId: latest?.node_id, version: latest?.version || 1, status, annotationCount: Number(latest?.annotation_count) || latest?.comments?.length || latest?.annotations?.length || 0, allNodes: nodes }
      }).filter(Boolean)
    },
    async loadJobAcl() {
      const laneId = this.jobData?.lane_id
      if (!laneId) {
        this.jobAcl = 0
        return
      }

      try {
        const laneResponse = await LaneAPI.getLane(laneId)
        const poolId = laneResponse.data?.pool_id
        if (!poolId) {
          this.jobAcl = 0
          return
        }

        const result = await PoolACLAPI.getPoolACLsFromUserGroup()
        this.jobAcl = (result.data.items || [])
          .filter(acl => acl.pool_id == poolId)
          .reduce((combined, acl) => combined | (parseInt(acl.acl) || 0), 0)
      } catch (error) {
        console.error('Error loading job ACL:', error)
        this.jobAcl = 0
      }
    },
    statusTitle(id) { return this.statusOptions.find(status => status.status_id == id)?.status_title || this.$t('draft') },
    versionLabel(version) { return `v${String(version).padStart(2, '0')}` },
    approvalIcon(status) {
      if (status === 'approved') return 'mdi-check-bold'
      if (status === 'rejected') return 'mdi-close-thick'
      return 'mdi-clock-outline'
    },
    formatCurrency(value) { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value) },
    toggleAll() { this.itemsPerPage = this.allRowsVisible ? 10 : -1 },
    routeParams() { return { appId: this.$route.params.appId, jobId: this.effectiveJobId, contentAppId: this.$route.params.contentAppId, businessAppId: this.$route.params.businessAppId } },
    goTo(name) {
      if (this.embedded) {
        const events = {
          JobDeliverableManagement: 'open-deliverable-management',
          JobDeliverableAssets: 'open-deliverable-assets'
        }
        if (events[name]) {
          this.$emit(events[name])
          return
        }
      }
      this.$router.push({ name, params: this.routeParams() })
    },
    goToUpload() { this.goTo('JobMultiFileUpload') },
    openNewVersionUpload(item) {
      if (!this.canEditMarkup) return
      this.assetForVersionUpload = { ...item.asset, formatInstance: item.formatInstance }
      this.$nextTick(() => this.$refs.versionUploader.uploadNewVersion(this.assetForVersionUpload))
    },
    openApproval(asset) { this.selectedAsset = asset; this.approvalDialog = true },
    updateMarkupCount({ sessionId, count }) {
      this.baseInstances = this.baseInstances.map(base => ({
        ...base,
        formatInstances: (base.formatInstances || []).map(format => ({
          ...format,
          assets: (format.assets || []).map(asset => {
            if (String(asset.sessionId) !== String(sessionId)) return asset

            return { ...asset, annotationCount: count }
          })
        }))
      }))
    },
    download(asset) { if (asset.nodeId) this[ContentActions.DOWNLOAD_FILE]({ nodeId: asset.nodeId, control: { downloading: false } }) }
  }
}
</script>

<style scoped>
.assets-list-container { padding: 24px; }
.assets-list-header { gap: 8px; }
.assets-list-toggle { margin-left: auto; }
.assets-list-nav { flex: 0 0 auto; }
.header-divider { border-width: 0 2px 0 0; max-height: 32px; align-self: center; }
.assets-table ::v-deep .v-data-table__wrapper { overflow-x: auto; }
.assets-table ::v-deep table { min-width: 920px; }
.assets-table ::v-deep th { white-space: nowrap; }
.assets-table ::v-deep td { white-space: normal; vertical-align: middle; }
.asset-name { display: inline-block; max-width: 260px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; white-space: nowrap; }
.asset-badges { display: inline-flex; align-items: center; gap: 6px; vertical-align: middle; }
.asset-version-badge { display: inline-flex; align-items: center; height: 20px; padding: 0 5px; border-radius: 3px; background: #b7e4bf; color: #164b20; font-size: 11px; font-weight: 700; line-height: 1; }
.asset-approval-badge { display: inline-flex; width: 24px; height: 24px; align-items: center; justify-content: center; border-radius: 3px; }
.asset-approval-badge--rejected { background: #fde1e1; color: #f44336; }
.asset-approval-badge--approved { background: #dcefdc; color: #43a047; }
.asset-approval-badge--pending { background: #e0e0e0; color: #616161; }
.asset-approval-badge ::v-deep .asset-approval-icon { color: currentColor; font-size: 19px; font-weight: 900; }
.approval-count-badge ::v-deep .v-badge__badge { top: -5px; right: -11px; min-width: 17px; height: 17px; padding: 1px 4px; border: 0; border-radius: 50%; background: #ffcdd2 !important; color: #f44336; font-size: 10px; font-weight: 700; line-height: 15px; }

@media (max-width: 960px) {
  .assets-list-container { padding: 12px; }
  .assets-list-header { align-items: flex-start; }
  .assets-list-header .flex-grow-1 { min-width: 180px; }
}

@media (max-width: 600px) {
  .assets-list-container { padding: 4px; }
  .assets-list-header { padding: 12px !important; }
  .assets-list-toggle { margin-left: 0; }
  .assets-list-toggle-label { display: none; }
  .asset-name { max-width: 180px; }
  ::v-deep .asset-approval-dialog { margin: 12px; max-height: calc(100vh - 24px) !important; }
  ::v-deep .asset-approval-dialog .v-card__text { height: calc(100vh - 110px) !important; }
}
</style>
